import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage, getLocalizedField } from '../context/LanguageContext';
import { usePatientFlow } from '../context/PatientFlowContext';
import { useAuth } from '../context/AuthContext';
import { useCategoryManagement } from '../context/CategoryManagementContext';
import { useTreatmentManagement } from '../context/TreatmentManagementContext';
import { CheckCircle, Shield, Globe, Sparkles, ArrowRight, Star, Search, MapPin, DollarSign, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import api, { corporateIdentityAPI } from '../utils/api';
import { useTranslation } from 'react-i18next';
import CorporateCertificates from '../components/CorporateCertificates';
// OF-001D — workspace-aware "My Dashboard" route resolution.
import { useRBAC } from '../context/RBACContext';
import { resolveDashboardRouteForActiveWorkspace } from '../utils/dashboardRouteResolver';
// TCPN-CN1 — every outbound `?country=` param must be canonical ISO-2.
import { normalizeCountryToISO2 } from '../utils/countryNormalize';
import SEO from '../components/SEO';
import { useHomepageSeoProps } from './homepageSeo';

const LandingPage = () => {
  const { language } = useLanguage();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { flowState, getNextStep, startNewJourney } = usePatientFlow();
  const { user } = useAuth();
  const { getActiveCategories } = useCategoryManagement();
  const { getActiveTreatments } = useTreatmentManagement();
  // OF-001D — pull the active workspace + catalogue so the "My Dashboard"
  // CTA below routes to the correct dashboard for the current workspace.
  const { activeWorkspace, availableWorkspaces, activeWorkspaceId } = useRBAC();
  
  // Anonymous pre-search state
  const [selectedTreatment, setSelectedTreatment] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedBudget, setSelectedBudget] = useState('');

  // Get dynamic categories and treatments
  const activeCategories = getActiveCategories();
  const activeTreatments = getActiveTreatments();

  // Build dynamic treatments from categories for landing page
  const dynamicTreatments = activeCategories.slice(0, 7).map(category => {
    // Match treatments by category slug or name (treatment_content may use either)
    const catId = category.id.toLowerCase();
    const catName = getLocalizedField(category.name, 'en').toLowerCase();
    // Only count published treatments (exclude coming_soon) for accurate display
    const publishedTreatments = activeTreatments.filter(t => {
      const tc = (t.category || '').toLowerCase();
      const isPublished = t.status === 'published';
      return isPublished && (tc === catId || tc === catName || tc.includes(catId));
    });
    const treatmentCount = publishedTreatments.length;
    return {
      name: getLocalizedField(category.name, 'en'),
      icon: category.emoji,
      image: category.image || '',
      count: treatmentCount > 0 ? `${treatmentCount} Treatment${treatmentCount !== 1 ? 's' : ''}` : 'Browse',
      slug: category.id,
      categoryId: category.id
    };
  });

  // Fallback only when API returns no categories (shows generic labels, no fake counts)
  const treatments = dynamicTreatments.length > 0 ? dynamicTreatments : [
    { name: 'Dental', icon: '🦷', count: 'Browse', slug: 'dental-veneers' },
    { name: 'Cosmetic', icon: '✨', count: 'Browse', slug: 'rhinoplasty' },
    { name: 'Hair', icon: '💇', count: 'Browse', slug: 'hair-transplant-fue-dhi' },
  ];
  
  // Compute banner visibility based on flow state OR authenticated patient
  const isPatient = user?.role === 'patient' || user?.user_type === 'patient';
  const hasActiveFlow = flowState.selectedTreatment || flowState.intakeCompleted || flowState.clinicsSelected.length > 0;
  const showContinueBanner = hasActiveFlow || isPatient;

  const getFlowStepLabel = () => {
    if (flowState.paymentCompleted) return 'View your dashboard';
    if (flowState.selectedOffer) return 'Complete your payment';
    if (flowState.clinicsSelected.length > 0) return 'Review your offers';
    if (flowState.intakeCompleted) return 'Select clinics for offers';
    if (flowState.selectedTreatment) return 'Complete your health profile';
    return 'Browse treatments';
  };

  // CMS Content state — the CANONICAL corporate certificate SSOT is
  // `/api/public/corporate-certificates` (state: `corporateCertificates`
  // below). We used to also read a marketing "certifications" list off
  // /public/landing-content and render it under a "Trusted & Certified"
  // strip — that block duplicated ISO 9001 (already in the canonical CMS)
  // and mixed clinic-side accreditations (JCI / TEMOS) with company-side
  // credentials. The Homepage now presents ONE coherent trust surface.
  // The admin CMS records under `/admin/landing-content` are intentionally
  // preserved on the backend in case another surface consumes them, but
  // the public Homepage no longer renders them.
  const [platformStats, setPlatformStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  // Canonical Corporate Certificates (SSOT: treatx_corporate_certificates).
  const [corporateCertificates, setCorporateCertificates] = useState([]);
  const [cmsAnnouncements, setCmsAnnouncements] = useState([]);
  const [cmsTrustContent, setCmsTrustContent] = useState([]);
  const [cmsFeatures, setCmsFeatures] = useState([]);
  const [cmsHeroItems, setCmsHeroItems] = useState([]);
  const [activeHeroIdx, setActiveHeroIdx] = useState(0);
  // TREATX-OWNER-ACCEPTANCE-P0-INVITE-LOGIN-SEARCH-01 — dynamic hero
  // search dropdowns. Countries come from the canonical active-provider
  // catalog (falls back to the full ISO catalog when no live providers
  // are approved yet). Treatments come from the canonical active-provider
  // treatment catalog (falls back to the static `activeTreatments`
  // catalog already imported above).
  const [searchCountries, setSearchCountries] = useState([]);
  const [searchTreatments, setSearchTreatments] = useState([]);
  
  // Derived: current active hero from rotation
  const cmsHero = cmsHeroItems.length > 0 ? cmsHeroItems[activeHeroIdx] || cmsHeroItems[0] : null;
  
  // Fetch CMS content on mount
  useEffect(() => {
    const fetchCmsContent = async () => {
      try {
        // Fetch platform stats — single source of truth
        const statsRes = await api.get(`/public/platform-stats`);
        const statsData = statsRes.data;
        if (statsData?.success && statsData.stats) {
          setPlatformStats(statsData.stats);
        }
      } catch (error) {
        console.warn('Platform stats unavailable, using safe fallbacks');
      } finally {
        setStatsLoading(false);
      }
      
      try {
        // Landing-content CMS still powers announcements, trust_content,
        // hero_content, and feature_content. Certifications are handled
        // by the canonical Corporate Certificates SSOT below — the
        // legacy `data.certifications` list is intentionally ignored on
        // the Homepage to avoid two authorities.
        const lcRes = await api.get(`/public/landing-content`);
        const data = lcRes.data;
        if (data?.success) {
          if (data.announcements?.length) setCmsAnnouncements(data.announcements.map(a => ({
            ...a,
            title: getLocalizedField(a.title, language),
            description: getLocalizedField(a.description, language),
          })));
          if (data.trust_content?.length) setCmsTrustContent(data.trust_content.map(tc => ({
            ...tc,
            title: getLocalizedField(tc.title, language),
            description: getLocalizedField(tc.description, language),
          })));
          if (data.hero_content?.length) {
            setCmsHeroItems(data.hero_content.map(hero => ({
              ...hero,
              title: getLocalizedField(hero.title, language),
              description: getLocalizedField(hero.description, language),
            })));
          }
          if (data.feature_content?.length) setCmsFeatures(data.feature_content.map(f => ({
            ...f,
            title: getLocalizedField(f.title, language),
            description: getLocalizedField(f.description, language),
          })));
        }
      } catch (error) {
        console.warn('Landing content blocks unavailable');
      }

      // Canonical Corporate Certificates (SSOT). Silent-fallback on error —
      // homepage must still render if the CMS endpoint is unreachable.
      try {
        const ccRes = await corporateIdentityAPI
          .listCertificates({ surface: 'homepage' })
          .catch(() => null);
        const list = ccRes?.data?.certificates;
        if (Array.isArray(list)) setCorporateCertificates(list);
      } catch (_err) { /* silent */ }

      // TREATX-OWNER-ACCEPTANCE-P0-INVITE-LOGIN-SEARCH-01 — dynamic
      // hero dropdowns. All failures fall through to the static
      // fallbacks already wired below so a network blip never empties
      // the dropdowns.
      try {
        const [acRes, atRes] = await Promise.all([
          api.get('/public/catalog/active-provider-countries').catch(() => null),
          api.get('/public/catalog/active-provider-treatments').catch(() => null),
        ]);
        const acData = Array.isArray(acRes?.data?.countries) ? acRes.data.countries : [];
        if (acData.length > 0) {
          setSearchCountries(acData);
        } else {
          const fullRes = await api.get('/public/catalog/countries-full').catch(() => null);
          const full = Array.isArray(fullRes?.data?.countries) ? fullRes.data.countries : [];
          setSearchCountries(full);
        }
        const atData = Array.isArray(atRes?.data?.treatments) ? atRes.data.treatments : [];
        setSearchTreatments(atData);
      } catch (_err) {
        // Silent — render falls back to static lists.
      }
    };
    
    fetchCmsContent();
  }, []);

  // TREATX-ANNOUNCEMENTS-PROMINENCE-01 — announcements used to cycle through a
  // 2 px-tall amber strip above the hero, which the Owner found too small to
  // read. They are now rendered as a full card section below the hero (all
  // active records at once, CMS order preserved), so no rotation timer is
  // needed. The CMS records, their date windows and publish behaviour are
  // untouched — only the presentation changed.

  // P1-020: Cycle through hero content every 8 seconds when multiple exist
  useEffect(() => {
    if (cmsHeroItems.length <= 1) return;
    const timer = setInterval(() => {
      setActiveHeroIdx(prev => (prev + 1) % cmsHeroItems.length);
    }, 8000);
    return () => clearInterval(timer);
  }, [cmsHeroItems.length]);

  
  // Stats display: API (override or live) → safe fallback (never hardcoded marketing numbers)
  // TR-LAUNCH-01: Average Rating removed until a defensible production
  // calculation exists (no verified review dataset in preview). Layout
  // rebalanced to a three-metric grid to avoid a hole in the hero.
  const displayStats = platformStats ? [
    { number: platformStats.providers?.display || `${platformStats.providers?.value || 0}+`, label: platformStats.providers?.label || 'Verified Clinics' },
    { number: platformStats.countries?.display || `${platformStats.countries?.value || 0}+`, label: platformStats.countries?.label || 'Countries' },
    { number: platformStats.patients?.display || `${platformStats.patients?.value || 0}+`, label: platformStats.patients?.label || 'Happy Patients' },
  ] : [
    { number: statsLoading ? '...' : '–', label: 'Verified Clinics' },
    { number: statsLoading ? '...' : '–', label: 'Countries' },
    { number: statsLoading ? '...' : '–', label: 'Happy Patients' },
  ];

  const defaultFeatures = [
    {
      icon: <Sparkles className="w-5 h-5" />,
      title: 'AI-Powered Matching',
      description: 'Our intelligent system matches you with the perfect clinic based on your needs.'
    },
    {
      icon: <Shield className="w-5 h-5" />,
      title: 'Verified Providers',
      description: 'All clinics are thoroughly verified and certified by TreatX.'
    },
    {
      icon: <Globe className="w-5 h-5" />,
      title: 'Global Network',
      description: 'Access world-class medical care across our global network.'
    },
  ];

  const FEATURE_ICON_MAP = {
    sparkles: <Sparkles className="w-5 h-5" />,
    shield: <Shield className="w-5 h-5" />,
    globe: <Globe className="w-5 h-5" />,
  };

  const features = cmsFeatures.length > 0
    ? cmsFeatures.map(f => ({
        icon: FEATURE_ICON_MAP[f.button_text] || <Sparkles className="w-5 h-5" />,
        title: f.title,
        description: f.description,
      }))
    : defaultFeatures;

  // Handle anonymous pre-search — unified to /find-clinics
  // Use treatment slug (not name) for canonical identifier across all pages
  const handleQuickSearch = () => {
    const params = new URLSearchParams();
    if (selectedTreatment) {
      const treatmentObj = activeTreatments.find(t => t.name === selectedTreatment);
      params.set('treatment', treatmentObj?.slug || selectedTreatment);
    }
    if (selectedCountry) {
      // TCPN-CN1: emit ISO-2 only. The dropdown values are already ISO-2,
      // but we route through the normalizer so any legacy/programmatic
      // value (e.g. a CMS-controlled link emitting "Turkey") collapses to
      // the canonical code. Unknown values are dropped (no `country=` set)
      // rather than echoed, which prevents the "looks-active filter that
      // returns 0" failure mode owner audits flagged.
      const iso = normalizeCountryToISO2(selectedCountry);
      if (iso) params.set('country', iso);
    }
    // TCPN S1-D — translate the budget preset enum → the canonical URL
    // contract consumed by SearchV2.
    //
    // V1V3-PARITY-01 (proven defect): this used to emit
    // `min_price` / `max_price` / `currency`. Those are the *API* parameter
    // names (SearchV2Page builds them at call time from its own state); the
    // *URL* contract it hydrates from is `budget_min` / `budget_max`
    // (SearchV2Page hydration + writeback). So every budget chosen on the
    // published homepage was silently dropped on arrival — the exact
    // "looks-active filter that returns the unfiltered set" failure mode.
    // Open-ended ranges still drop the missing bound ("10000+" → min only).
    if (selectedBudget) {
      let lo = null;
      let hi = null;
      if (selectedBudget.endsWith('+')) {
        const v = parseInt(selectedBudget.slice(0, -1), 10);
        if (Number.isFinite(v)) lo = v;
      } else if (selectedBudget.includes('-')) {
        const [a, b] = selectedBudget.split('-').map((s) => parseInt(s, 10));
        if (Number.isFinite(a)) lo = a;
        if (Number.isFinite(b)) hi = b;
      }
      if (lo !== null) params.set('budget_min', String(lo));
      if (hi !== null) params.set('budget_max', String(hi));
    }
    navigate(`/find-clinics${params.toString() ? '?' + params.toString() : ''}`);
  };

  const handleTreatmentClick = (treatment) => {
    const param = treatment.slug || treatment.categoryId || treatment.name;
    navigate(`/find-clinics?treatment=${encodeURIComponent(param)}`);
  };

  // TREATX-SEO-RECOVERY-S1 — the shared homepage head authority. Same values
  // this page carried inline before; now also used by V2/V3 so publishing a
  // different version cannot change the homepage's metadata. `/v1` renders
  // this page as a preview and is therefore noindex + canonical `/`.
  const homepageSeo = useHomepageSeoProps();

  return (
    <div className="min-h-screen bg-background">
      <SEO {...homepageSeo} />
      {/* Continue Journey Banner */}
      {showContinueBanner && (
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white py-2 px-4" data-testid="continue-journey-banner">
          <div className="container-custom flex items-center justify-between">
            <p className="text-sm">
              {hasActiveFlow ? (
                <><span className="font-medium">Continue your journey:</span> {flowState.selectedTreatment && `${flowState.selectedTreatment} • `}{getFlowStepLabel()}</>
              ) : (
                <span className="font-medium">Welcome back! Your treatment journey awaits.</span>
              )}
            </p>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => navigate(
                hasActiveFlow
                  ? getNextStep()
                  : resolveDashboardRouteForActiveWorkspace(activeWorkspace, availableWorkspaces, activeWorkspaceId)
              )}
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              data-testid="continue-journey-btn"
            >
              {hasActiveFlow ? 'Continue' : 'My Dashboard'} <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* CMS Announcements — prominent card section (TREATX-ANNOUNCEMENTS-PROMINENCE-01).
          Reads the SAME `/public/landing-content` announcement records as
          before: CMS order, date windows and publish state decide what shows. */}
      {cmsAnnouncements.length > 0 && (
        <section className="bg-amber-50/60 border-b border-amber-100 py-8 sm:py-10" data-testid="announcement-section">
          <div className="container-custom">
            <div className="flex items-center gap-2 mb-4 sm:mb-5">
              <Sparkles className="h-5 w-5 text-amber-600 shrink-0" />
              <h2 className="text-base md:text-lg font-semibold text-slate-900" data-testid="announcement-section-heading">
                {t('landing.announcementsHeading', 'Announcements')}
              </h2>
            </div>
            <div className={`grid gap-5 sm:gap-6 ${cmsAnnouncements.length === 1 ? 'grid-cols-1 max-w-3xl' : cmsAnnouncements.length === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
              {cmsAnnouncements.map((ann, idx) => {
                const annImg = ann.compressed_url || ann.thumbnail_url || ann.image_url;
                return (
                  <article
                    key={ann.block_id || `${ann.title}-${idx}`}
                    className="bg-white rounded-xl border border-amber-200/70 shadow-sm overflow-hidden flex flex-col transition-shadow hover:shadow-md"
                    data-testid={`announcement-card-${idx}`}
                  >
                    {annImg && (
                      <img
                        src={annImg}
                        alt={ann.image_alt || ''}
                        className="w-full aspect-[16/9] object-cover"
                        loading="lazy"
                        onError={(e) => { e.target.style.display = 'none'; }}
                        data-testid={`announcement-card-${idx}-image`}
                      />
                    )}
                    <div className="p-5 sm:p-6 flex flex-col gap-2 flex-1">
                      <h3 className="text-base md:text-lg font-semibold text-slate-900 leading-snug" data-testid={`announcement-card-${idx}-title`}>
                        {ann.title}
                      </h3>
                      {ann.description && (
                        <p className="text-sm text-slate-600 leading-relaxed" data-testid={`announcement-card-${idx}-body`}>
                          {ann.description}
                        </p>
                      )}
                      {ann.button_text && ann.button_link && (
                        <div className="mt-auto pt-3">
                          <Button
                            size="sm"
                            onClick={() => navigate(ann.button_link)}
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                            data-testid={`announcement-card-${idx}-cta`}
                          >
                            {ann.button_text} <ArrowRight className="h-3 w-3 ml-1" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Hero Section - Compact */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 text-white">
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        
        <div className="container-custom relative py-12 lg:py-16">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Left - Hero Content */}
            <div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 leading-tight tracking-tight" data-testid="hero-title">
                {cmsHero ? cmsHero.title : t('landingHero.title')}
              </h1>
              <p className="text-base text-blue-100 mb-6 max-w-lg" data-testid="hero-subtitle">
                {cmsHero ? cmsHero.description : t('landingHero.subtitle')}
              </p>
              
              {/* Stats - Compact */}
              <div className="grid grid-cols-4 gap-4 mb-6" data-testid="landing-stats-grid">
                {displayStats.map((stat, index) => (
                  <div key={index} className="text-center" data-testid={`landing-stat-${index}`}>
                    <div className="text-xl lg:text-2xl font-bold text-white" data-testid={`landing-stat-value-${index}`}>{stat.number}</div>
                    <div className="text-xs text-blue-200">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - Quick Search (Anonymous Pre-Search) */}
            <Card className="bg-white/10 backdrop-blur-lg border-white/20">
              <CardContent className="p-5">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Search className="h-4 w-4" />
                  Find Your Treatment
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-blue-200 mb-1 block">Treatment Type</label>
                    <select
                      value={selectedTreatment}
                      onChange={(e) => setSelectedTreatment(e.target.value)}
                      className="w-full bg-white/10 border border-white/20 rounded-lg p-2.5 text-white text-sm focus:ring-2 focus:ring-blue-400"
                      data-testid="hero-treatment-select"
                    >
                      <option value="" className="text-gray-900">Select treatment...</option>
                      {/* TREATX-OWNER-ACCEPTANCE-P0-INVITE-LOGIN-SEARCH-01:
                          Prefer the canonical active-provider treatment
                          catalog (dynamic, real provider offerings); fall
                          back to the existing static `activeTreatments`
                          when the dynamic list is empty so the dropdown
                          is NEVER empty during owner UAT. */}
                      {(searchTreatments.length > 0
                        ? searchTreatments.map(t => ({ key: t.slug, value: t.slug, label: t.name }))
                        : activeTreatments.map(t => ({ key: t.id, value: t.name, label: t.name }))
                      ).map(o => (
                        <option key={o.key} value={o.value} className="text-gray-900">{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-blue-200 mb-1 block">Country</label>
                      <select
                        value={selectedCountry}
                        onChange={(e) => setSelectedCountry(e.target.value)}
                        className="w-full bg-white/10 border border-white/20 rounded-lg p-2.5 text-white text-sm"
                        data-testid="hero-country-select"
                      >
                        <option value="" className="text-gray-900">Any country</option>
                        {/* TREATX-OWNER-ACCEPTANCE-P0-INVITE-LOGIN-SEARCH-01:
                            Dynamic, canonical ISO-2 country list derived
                            from active providers (or the full ISO catalog
                            fallback). Replaces the previous hardcoded
                            6-country subset. */}
                        {searchCountries.map(c => (
                          <option key={c.code} value={c.code} className="text-gray-900">{c.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-blue-200 mb-1 block">Budget</label>
                      <select
                        value={selectedBudget}
                        onChange={(e) => setSelectedBudget(e.target.value)}
                        className="w-full bg-white/10 border border-white/20 rounded-lg p-2.5 text-white text-sm"
                      >
                        <option value="" className="text-gray-900">Any budget</option>
                        <option value="0-2000" className="text-gray-900">Under €2,000</option>
                        <option value="2000-5000" className="text-gray-900">€2,000 - €5,000</option>
                        <option value="5000-10000" className="text-gray-900">€5,000 - €10,000</option>
                        <option value="10000+" className="text-gray-900">€10,000+</option>
                      </select>
                    </div>
                  </div>
                  <Button 
                    onClick={handleQuickSearch}
                    className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white py-2.5"
                  >
                    <Search className="h-4 w-4 mr-2" />
                    Explore Clinics
                  </Button>
                  <p className="text-xs text-blue-200 text-center">
                    No registration required • Compare freely
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Section - Compact */}
      <section className="py-10 bg-white border-b">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-start gap-3 p-4 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 flex-shrink-0">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-1">{feature.title}</h3>
                  <p className="text-sm text-gray-600">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Canonical Corporate Certificates — granted vs application-in-progress. */}
      {corporateCertificates.length > 0 && (
        <section className="py-10 bg-white border-b" data-testid="corporate-certificates-section">
          <div className="container-custom">
            <div className="text-center mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-1">
                {t('corporateIdentity.homeHeading', 'Certifications & Authorizations')}
              </h2>
              <p className="text-sm text-slate-600 max-w-2xl mx-auto">
                {t('corporateIdentity.homeSubheading', 'What we hold today, and what we have applied for.')}
              </p>
            </div>
            <CorporateCertificates
              certificates={corporateCertificates}
              variant="homepage"
              labels={{
                grantedHeading: t('corporateIdentity.certGrantedHeading', 'Certified & Authorized'),
                grantedCaption: t('corporateIdentity.certGrantedCaption', 'Certificates currently held by the company.'),
                pendingHeading: t('corporateIdentity.certPendingHeading', 'Authorizations in Progress'),
                pendingCaption: t('corporateIdentity.certPendingCaption', 'These authorizations have been applied for and are not yet granted.'),
                pending: t('corporateIdentity.pending', 'Application in progress'),
                clickToEnlarge: t('corporateIdentity.clickToEnlarge', 'Click to enlarge'),
                lightbox: {
                  pending: t('corporateIdentity.pending', 'Application in progress'),
                  openPdf: t('corporateIdentity.openPdf', 'Open PDF'),
                  pdfNotice: t('corporateIdentity.pdfNotice', 'This certificate is only available as a PDF document.'),
                  noAsset: t('corporateIdentity.noAsset', 'A copy of this certificate has not been published yet.'),
                  noAssetPending: t('corporateIdentity.noAssetPending', 'The certificate document will be published here once the authorization is granted.'),
                  certNumber: t('corporateIdentity.certNumber', 'Certificate no.'),
                  scope: t('corporateIdentity.scope', 'Scope'),
                  issueDate: t('corporateIdentity.issueDate', 'Issued'),
                  validUntil: t('corporateIdentity.validUntil', 'Valid until'),
                },
              }}
            />
          </div>
        </section>
      )}

      {/* CMS Trust Content Section */}
      {cmsTrustContent.length > 0 && (
        <section className="py-10 bg-white" data-testid="trust-content-section">
          <div className="container-custom">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Why Trust TreatX</h2>
              <p className="text-gray-600 text-sm">Your safety and confidence are our top priority</p>
            </div>
            <div className={`grid gap-4 ${cmsTrustContent.length === 1 ? 'md:grid-cols-1 max-w-md mx-auto' : cmsTrustContent.length === 2 ? 'md:grid-cols-2 max-w-2xl mx-auto' : cmsTrustContent.length === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-3'}`}>
              {cmsTrustContent.map((item, idx) => (
                <div key={item.block_id || idx} className="bg-slate-50 rounded-xl p-5 border border-slate-100 text-center"
                  data-testid={`trust-block-${idx}`}>
                  {(item.thumbnail_url || item.compressed_url || item.image_url) ? (
                    <img src={item.thumbnail_url || item.compressed_url || item.image_url} alt={item.title}
                      className="h-10 w-10 rounded-full object-cover mx-auto mb-3"
                      onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-[#3BA4C5]/10 flex items-center justify-center mx-auto mb-3">
                      <Shield className="h-5 w-5 text-[#3BA4C5]" />
                    </div>
                  )}
                  <h3 className="text-sm font-semibold text-[#122033] mb-1">{item.title}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Treatments Section - Dynamic from Categories */}
      <section className="py-10 bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Popular Treatments</h2>
            <p className="text-gray-600 text-sm">
              Browse our most sought-after medical procedures
            </p>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {treatments.map((treatment, index) => (
              <button
                key={index}
                onClick={() => handleTreatmentClick(treatment)}
                className="bg-white rounded-lg overflow-hidden text-center hover:shadow-md transition-all hover:-translate-y-0.5 border border-gray-100"
                data-testid={`treatment-card-${treatment.categoryId || index}`}
              >
                {treatment.image ? (
                  <div className="h-20 w-full overflow-hidden">
                    <img src={treatment.image} alt={treatment.name} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                ) : (
                  <div className="h-20 w-full flex items-center justify-center bg-gray-50">
                    <span className="text-3xl">{treatment.icon}</span>
                  </div>
                )}
                <div className="p-2">
                  <div className="font-medium text-gray-900 text-sm mb-0.5">{treatment.name}</div>
                  <div className="text-xs text-gray-500">{treatment.count}</div>
                </div>
              </button>
            ))}
          </div>
          <div className="text-center mt-6">
            <Button
              variant="outline"
              onClick={() => navigate('/treatments')}
              className="text-sm"
            >
              View All Treatments <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section - Compact */}
      <section className="py-10 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="container-custom text-center">
          <h2 className="text-2xl font-bold mb-2">Ready to Start Your Journey?</h2>
          <p className="text-blue-100 mb-6 text-sm max-w-lg mx-auto">
            Join thousands of patients who found their perfect medical provider through TreatX
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button
              onClick={() => navigate('/find-clinics')}
              className="bg-white text-blue-600 hover:bg-blue-50"
              data-testid="landing-find-clinics-btn"
            >
              Find Clinics
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/login')}
              className="border-white text-white hover:bg-white/10"
            >
              Get Personalized Offer
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
